import { DataTable } from "../components";
import type { DataTableProps } from "../components/DataTable/DataTable";
import appStyles from "../Overview.module.css";
import type { Column, StatsSectionUsageData } from "../types";
import { humanMemSize } from "../utils";
import { GetGraphDashboardAccountRow } from "./graphs";
import styles from "./StatsSection.module.css";

export interface Config {
  quotaConf?: DataTableProps;
  accountUsageConf?: DataTableProps;
  modelsCPUConf?: DataTableProps;
  modelsGPUConf?: DataTableProps;
  biggestJobsConf?: DataTableProps;
}

interface StatsSectionProps {
  title?: string;
  stats: StatsSectionUsageData;
  graphURL?: string;
}

const boundMemTransform = (
  val: number,
  _col: Column,
  _index: number,
  _len: number,
) => {
  // Round to nearest integer, use placeholder from column
  // and don't show units.
  return humanMemSize(val, undefined, undefined, false);
};

const roundNear = (
  val: number | undefined,
  _col: Column,
  _index: number,
  _len: number,
) => {
  return typeof val !== "undefined" && !Number.isInteger(val)
    ? val.toFixed(3)
    : val;
};

export function StatsSectionUsageTotal({
  title = "Total Resources",
  stats,
}: StatsSectionProps) {
  const configs: Record<string, Config> = {};

  Object.entries(stats).forEach(([key, colConf]) => {
    const { name: typeTitle, data } = colConf;

    configs[key] = {
      quotaConf: {
        title: `Totals - ${typeTitle}`,
        headers: ["", "Nº Jobs", "CPU", "GPU", "Memory (GB)"],
        cols: [
          { key: "rtype" },
          {
            key: "njobs",
            transform: (val: any) => (typeof val === "undefined" ? "" : val),
          },
          { key: "cpu", transform: roundNear },
          { key: "gpu", transform: roundNear },
          { key: "mem", transform: boundMemTransform },
        ],
        data: [
          data.resources.total,
          data.resources.used,
          data.resources.npUsed,
          data.resources.free,
        ],
      },
    };
  });

  return (
    <>
      <h3 className={styles.subTitle}>{title}</h3>
      <div className={styles.sectionContainer}>
        {configs.pStats.quotaConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.pStats.quotaConf} />
          </div>
        )}
        <div className={appStyles.spacer} />

        {configs.NPStats.quotaConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.NPStats.quotaConf} />
          </div>
        )}
      </div>
    </>
  );
}

export function StatsSectionUsage({
  title = "Resources",
  stats,
  graphURL,
}: StatsSectionProps) {
  const configs: Record<string, Config> = {};

  Object.entries(stats).forEach(([key, colConf]) => {
    const { name: typeTitle, data } = colConf;

    configs[key] = {
      accountUsageConf: {
        title: `Per Account - ${typeTitle}`,
        headers: ["Account", "Nº Jobs", "CPU", "GPU", "Memory (GB)"],
        cols: [
          {
            key: "name",
            url: graphURL
              ? GetGraphDashboardAccountRow.bind(null, graphURL)
              : undefined,
          },
          {
            key: "njobs",
            transform: (val: any) => (typeof val === "undefined" ? "" : val),
          },
          { key: "cpu", transform: roundNear },
          { key: "gpu", transform: roundNear },
          { key: "mem", transform: boundMemTransform },
        ],
        data: data.resources.users,
      },
    };
  });

  return (
    <>
      <h3 className={styles.subTitle}>{title}</h3>
      <div className={styles.sectionContainer}>
        {configs.pStats.accountUsageConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.pStats.accountUsageConf} />
          </div>
        )}
        <div className={appStyles.spacer} />

        {configs.NPStats.accountUsageConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.NPStats.accountUsageConf} />
          </div>
        )}
      </div>
    </>
  );
}

export function StatsSectionHardware({
  title = "Hardware",
  stats,
}: StatsSectionProps) {
  const configs: Record<string, Config> = {};

  Object.entries(stats).forEach(([key, colConf]) => {
    const { name: typeTitle, data } = colConf;

    configs[key] = {
      modelsCPUConf: {
        title: `CPUs - ${typeTitle}`,
        headers: ["Name", "Available", "Total"],
        cols: [
          { key: "name" },
          { key: "avail", transform: roundNear },
          { key: "total", transform: roundNear },
        ],
        data: Object.entries(data.models?.cpu || {}).map(([name, stats]) => {
          return {
            name,
            ...stats,
          };
        }),
      },

      modelsGPUConf: {
        title: `GPUs - ${typeTitle}`,
        headers: ["Name", "Available", "Total"],
        cols: [
          { key: "name" },
          { key: "avail", transform: roundNear },
          { key: "total", transform: roundNear },
        ],
        data: Object.entries(data.models?.gpu || {}).map(([name, stats]) => {
          return {
            name,
            ...stats,
          };
        }),
      },
    };
  });

  return (
    <>
      <h3 className={styles.subTitle}>{title}</h3>
      <div className={styles.sectionContainer}>
        {configs.pStats.modelsGPUConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.pStats.modelsGPUConf} />
          </div>
        )}

        <div className={appStyles.spacer} />

        {configs.NPStats.modelsGPUConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.NPStats.modelsGPUConf} />
          </div>
        )}
      </div>

      <div className={appStyles.vspacer} />

      <div className={styles.sectionContainer}>
        {configs.pStats.modelsCPUConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.pStats.modelsCPUConf} />
          </div>
        )}

        <div className={appStyles.spacer} />

        {configs.NPStats.modelsCPUConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.NPStats.modelsCPUConf} />
          </div>
        )}
      </div>
    </>
  );
}

export function StatsSectionBiggestJobs({
  title = "Biggest Jobs",
  stats,
}: StatsSectionProps) {
  const configs: Record<string, Config> = {};

  Object.entries(stats).forEach(([key, colConf]) => {
    const { name: typeTitle, data } = colConf;

    configs[key] = {
      biggestJobsConf: {
        title: `Jobs - ${typeTitle}`,
        headers: ["Type", "CPU", "GPU", "Memory (GB)"],
        cols: [
          { key: "name" },
          { key: "cpu", transform: roundNear },
          { key: "gpu", transform: roundNear },
          { key: "mem", transform: boundMemTransform },
        ],
        data: Object.entries(data.biggest_jobs || {}).map(([name, stats]) => {
          return {
            name,
            ...stats,
          };
        }),
      },
    };
  });

  return (
    <>
      <h3 className={styles.subTitle}>{title}</h3>
      <div className={styles.sectionContainer}>
        {configs.pStats.biggestJobsConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.pStats.biggestJobsConf} />
          </div>
        )}

        <div className={appStyles.spacer} />

        {configs.NPStats.biggestJobsConf && (
          <div className={styles.tableContainer}>
            <DataTable {...configs.NPStats.biggestJobsConf} />
          </div>
        )}
      </div>
    </>
  );
}
